
import React from 'react';
import { daypartDataBySegment } from './daypartData';
import { TrendingUp } from 'lucide-react';

const DaypartPeakPeriods = ({ segmentId }) => {
  const daypartData = daypartDataBySegment[segmentId] || daypartDataBySegment.urban;
  
  // Calculate peak periods for each customer type
  const calculatePeaks = () => {
    const residentialData = [...daypartData].sort((a, b) => b.residential - a.residential).slice(0, 3);
    const commercialData = [...daypartData].sort((a, b) => b.commercial - a.commercial).slice(0, 3);
    const commutersData = [...daypartData].sort((a, b) => b.commuters - a.commuters).slice(0, 3);
    
    return {
      residential: residentialData.map(item => item.hour),
      commercial: commercialData.map(item => item.hour),
      commuters: commutersData.map(item => item.hour)
    };
  };
  
  const peaks = calculatePeaks();
  
  // Segment-specific names for customer types
  const getCustomerTypeNames = () => {
    switch(segmentId) {
      case 'highway':
        return {
          residential: 'Local Travelers',
          commercial: 'Commercial Trucks',
          commuters: 'Long-Distance Travelers'
        };
      case 'rural':
        return {
          residential: 'Local Residents',
          commercial: 'Agricultural',
          commuters: 'Travelers'
        };
      case 'suburban':
        return {
          residential: 'Local Families',
          commercial: 'Neighborhood Businesses',
          commuters: 'Commuters'
        };
      case 'urban':
      default:
        return {
          residential: 'Local Residential',
          commercial: 'Commercial',
          commuters: 'Commuters'
        };
    }
  };
  
  const customerTypes = getCustomerTypeNames();
  
  const recommendedProductsBySegment = {
    urban: {
      residential: ['Premium Fuel', 'Grab-and-Go Food', 'Café Amazon'],
      commercial: ['Loyalty Program', 'Food Service', 'Car Care'],
      commuters: ['Premium Coffee', 'Quick Service Food', 'Loyalty App']
    },
    highway: {
      residential: ['Standard Fuel', 'Snacks', 'Travel Essentials'],
      commercial: ['Diesel', 'Fleet Services', 'Truck Amenities'],
      commuters: ['Premium Fuel', 'Coffee', 'Rest Stop Services']
    },
    rural: {
      residential: ['Everyday Essentials', 'Standard Fuel', 'Community Services'],
      commercial: ['Diesel', 'Agricultural Supplies', 'Extended Hours'],
      commuters: ['Maps/Navigation', 'Coffee', 'Quick Meals']
    },
    suburban: {
      residential: ['Family Meal Deals', 'Premium Fuel', 'Car Wash'],
      commercial: ['Standard Services', 'Quick Meals', 'Small Business Services'],
      commuters: ['Coffee Subscriptions', 'Quick Breakfast', 'Loyalty Programs']
    }
  };
  
  const recommendedProducts = recommendedProductsBySegment[segmentId] || recommendedProductsBySegment.urban;
  
  return (
    <div className="bg-white rounded-lg shadow-sm border border-gray-100 p-4">
      <div className="flex items-center text-md font-medium text-gray-900 mb-4">
        <TrendingUp size={16} className="mr-2 text-purple-700" />
        <h3>Peak Period Analysis</h3>
      </div>
      
      <div className="space-y-6">
        <div>
          <h4 className="text-sm font-medium text-gray-800 mb-2">Peak Hours by Customer Segment</h4>
          <div className="grid grid-cols-3 gap-3">
            {Object.entries(peaks).map(([key, hours]) => (
              <div key={key} className="bg-gray-50 p-3 rounded-lg">
                <h5 className="text-xs font-medium text-gray-700 mb-2">{customerTypes[key]}</h5>
                <div className="space-y-1">
                  {hours.map((hour, idx) => (
                    <div key={idx} className="flex items-center">
                      <div className={`w-2 h-2 rounded-full mr-2 ${
                        key === 'residential' ? 'bg-green-500' : 
                        key === 'commercial' ? 'bg-blue-500' : 'bg-orange-500'
                      }`}></div>
                      <span className="text-xs">{hour}</span>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
        
        <div>
          <h4 className="text-sm font-medium text-gray-800 mb-2">Recommended Products by Customer Type</h4>
          <div className="grid grid-cols-3 gap-3">
            {Object.entries(recommendedProducts).map(([key, products]) => (
              <div key={key} className="bg-gray-50 p-3 rounded-lg">
                <h5 className="text-xs font-medium text-gray-700 mb-2">{customerTypes[key]}</h5>
                <ul className="space-y-1">
                  {products.map((product, idx) => (
                    <li key={idx} className="text-xs text-gray-600 flex items-center">
                      <div className="w-1 h-1 rounded-full bg-gray-400 mr-2"></div>
                      {product}
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default DaypartPeakPeriods;
